import { BASE_PATH } from 'constants';
import AbstractModelType from '../AbstractModelType';
import BookLocation from '../BookLocation';

// A document represents an XHTML file of content which may contain any number of chapters or pages.
// It is the same as "SpineEntry" on the backend and in source epubs.
class BookDocument extends AbstractModelType {
  book;
  index;
  label;
  characterCount;
  hasMathML;
  hasCaseSwitches;
  fixedWidth;
  isFixedWidth = false;
  fixedHeight;
  isFixedHeight;
  percentageStart; // at what percentage through the book does this document appear
  percentageEnd; // at what percentage throught the book does this document end
  path; // path to the html within the source epub
  #legacyPk;
  #pages;
  #markers;

  constructor({ ...properties }) {
    super(...arguments);

    this.index = properties.index;
    this.number = properties.index + 1; // "number" for documents is just a 1-indexed version of the order
    this.label = `${this.number}`; // and "label" is just the number, but cast to a string

    // ONLY for internal links formatted with #sp
    // only books processed before sometime around 2020 still use #sp, newly processed ones use #po
    // if all books are reprocessed, this can be removed
    this.#legacyPk = properties.pk;

    this.characterCount = properties.characterCount;
    this.hasMathML = properties.hasMathML;
    this.hasCaseSwitches = properties.hasCaseSwitches;

    this.fixedWidth = properties.fixedWidth;
    this.isFixedWidth = Boolean(properties.fixedWidth);
    this.fixedHeight = properties.fixedHeight;
    this.isFixedHeight = Boolean(properties.fixedHeight);

    this.path = properties.path;
  }

  get sourceURL() {
    if ('readerServiceWorker' in window) {
      // This is not a real endpoint! The service worker will handle proxying to the real content.
      // iframe urls need to have the same root path as the service worker (/read/) for iframe subresources to work offline.
      return `${BASE_PATH}/document/${this.book.pk}/${this.index}`;
    } else {
      // If the service worker is not available, request the non-offline version of the document directly from the backend.
      return `/spine/${this.book.pk}/${this.number}`;
    }
  }

  get bookLocation() {
    return new BookLocation({ document: this });
  }

  get previous() {
    return this.book.documents[this.index - 1] ?? null;
  }

  get next() {
    return this.book.documents[this.index + 1] ?? null;
  }

  get pages() {
    if (this.#pages) {
      return this.#pages;
    }

    const pages = [];

    for (let page of this.book.pages) {
      if (page?.documents?.includes(this)) {
        pages.push(page);
      }
    }

    pages.sort((pageA, pageB) => pageA.index - pageB.index);

    this.#pages = pages;

    return this.#pages;
  }

  get markers() {
    if (this.#markers) {
      return this.#markers;
    }

    const markers = [];

    for (let marker of this.book.markers) {
      if (marker?.documents?.includes(this)) {
        markers.push(marker);
      }
    }

    markers.sort((markerA, markerB) => markerA.index - markerB.index);

    this.#markers = markers;

    return this.#markers;
  }

  get topLevelMarkers() {
    return this.markers.filter((marker) => marker.isTopLevelMarker);
  }

  get pk() {
    return this.#legacyPk;
  }

  get percentage() {
    return this.percentageEnd - this.percentageStart;
  }

  get charsInDocumentPages() {
    return this.pages.reduce((a, p) => a + p.characterCount, 0);
  }

  setDerivedProperties() {
    return [this.markers, this.pages];
  }
}

export default BookDocument;
